import { DropDownListOptionType } from 'types/common/CommonType'
import { ConnectorType, LibraryLogStatusType } from 'types/library/LibraryType'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface LibraryDataType {
  description: string
  libraryVersion: string
  lastModifiedDate: string
  libraryId: string
  libraryDetailId: string
  libraryName: string
  modifiedBy: string
  rows: string
  tagNameList: string[]
  publishStatus: boolean | null
  connectorType: ConnectorType | null
  permissions?: any
}

export interface VersionDataType {
  description: string
  lastModifiedDate: string
  libraryName: string
  libraryVersion: string
  libraryDetailId: string
  libraryId: string
  modifiedBy: string
  rows: string
  publishStatus: boolean | null
}

interface LibraryLogDataType {
  exportLogId: string
  exportName: string
  exportStatus: LibraryLogStatusType | null
  exportIssues: null
  timeStarted: string
  timeFinished: string
  exportedTo: string
  exportedLocation: null
  exportedBy: string
  sourceDatasetName: string
  sourceDatasetDescription: string
  sourceVersion: number
  sourceRows: number
  sourceDatasetCreatedDate?: string
}

interface LibraryDataStore {
  libraryData: LibraryDataType
  versionData: VersionDataType
  libraryLogData: LibraryLogDataType
  clickedVersionId: string
  searchTagsList: string[]
  searchDatasourcesList: DropDownListOptionType[]
  searchPermissionUsersList: DropDownListOptionType[]
  searchPermissionGroupsList: DropDownListOptionType[]
  initSearchTagsList: string[]
  getDatasetListTrigger: number
  socketData: { [key: string]: SocketDataType }

  selectedDataset: LibraryDataType | null

  setLibraryData: (newData: Partial<LibraryDataType>) => void
  setVersionData: (newData: Partial<VersionDataType>) => void
  setLibraryLogData: (newData: Partial<LibraryLogDataType>) => void
  resetLibraryLogData: () => void
  setClickedVersionId: (clickedVersionId: string) => void
  setSearchTagsList: (newData: string[]) => void
  addSearchTagsList: (addData: string) => void
  deleteSearchTagsList: (deleteData: string) => void

  setSearchDatasourcesList: (newData: DropDownListOptionType[]) => void
  setSearchPermissionUsersList: (newData: DropDownListOptionType[]) => void
  setSearchPermissionGroupsList: (newData: DropDownListOptionType[]) => void
  setInitSearchTagsList: (newData: string[]) => void

  setGetDatasetListTrigger: (isReset?: boolean) => void
  setSocketData: (newData: SocketDataType) => void

  setSelectedDataset: (dataset: LibraryDataType | null) => void

  resetSocketData: () => void
}

interface SocketDataType {
  libraryDetailId: string
  progress: string
  status: string
  totalCount: number
  currentCount: number
  chunkData: any
}

export const initLibraryDataInStore = {
  description: '',
  libraryVersion: '',
  lastModifiedDate: '',
  libraryId: '',
  libraryDetailId: '',
  libraryName: '',
  modifiedBy: '',
  rows: '',
  tagNameList: [],
  publishStatus: null,
  connectorType: null,
}

export const initVersionDataInStore = {
  description: '',
  lastModifiedDate: '',
  libraryDetailId: '',
  libraryName: '',
  libraryVersion: '',
  libraryId: '',
  modifiedBy: '',
  rows: '',
  publishStatus: null,
}

export interface EditDetailsLibraryVersionDataType {
  createdDate: string
  lastModifiedDate: string
  createdBy: string
  lastModifiedBy: string
  libraryId: string
  libraryDetailId: string
  libraryVersion: any
  libraryName: string
  description: string
  publishStatus: any
  numberOfProjects: any
  sourceType: any
  fileSize: any
  rows: any
  columns: any
}

export const initEditDetailsLibraryVersionDetailData = {
  createdDate: '',
  lastModifiedDate: '',
  createdBy: '',
  lastModifiedBy: '',
  libraryId: '',
  libraryDetailId: '',
  libraryVersion: '',
  libraryName: '',
  description: '',
  publishStatus: '',
  numberOfProjects: '',
  sourceType: '',
  fileSize: '',
  rows: '',
  columns: '',
}

const initLibraryLogData = {
  exportLogId: '',
  exportName: '',
  exportStatus: null,
  exportIssues: null,
  timeStarted: '',
  timeFinished: '',
  exportedTo: '',
  exportedLocation: null,
  exportedBy: '',
  sourceDatasetName: '',
  sourceDatasetDescription: '',
  sourceVersion: 0,
  sourceRows: 0,
  sourceDatasetCreatedDate: '',
}

const useLibraryDataStore = create<LibraryDataStore>((set) => ({
  libraryData: initLibraryDataInStore,
  versionData: initVersionDataInStore,
  libraryLogData: initLibraryLogData,
  clickedVersionId: '',

  searchTagsList: [],
  searchDatasourcesList: [],
  searchPermissionUsersList: [],
  searchPermissionGroupsList: [],

  initSearchTagsList: [],

  getDatasetListTrigger: 0,

  socketData: {},

  selectedDataset: null,

  setLibraryData: (newData: Partial<LibraryDataType>) => {
    set((state) => ({
      libraryData: { ...state.libraryData, ...newData },
    }))
  },
  setVersionData: (newData: Partial<VersionDataType>) =>
    set((state) => ({
      versionData: { ...state.versionData, ...newData },
    })),
  setLibraryLogData: (newData: Partial<LibraryLogDataType>) =>
    set((state) => ({
      libraryLogData: { ...state.libraryLogData, ...newData },
    })),

  resetLibraryLogData: () => {
    set((state) => ({
      libraryLogData: initLibraryLogData,
    }))
  },

  setClickedVersionId: (clickedVersionId: string) => set({ clickedVersionId: clickedVersionId }),

  setSearchTagsList: (newData: string[]) =>
    set((state) => ({
      searchTagsList: newData,
    })),

  setInitSearchTagsList: (newData: string[]) => set((state) => ({ initSearchTagsList: newData })),

  addSearchTagsList: (addData: string) =>
    set((state) => ({
      searchTagsList: [...state.searchTagsList, addData],
      initSearchTagsList: [...state.initSearchTagsList, addData],
    })),

  deleteSearchTagsList: (deleteData: string) =>
    set((state) => ({
      searchTagsList: [...state.searchTagsList.filter((item) => item !== deleteData)],
      initSearchTagsList: [...state.initSearchTagsList.filter((item) => item !== deleteData)],
    })),

  setSearchDatasourcesList: (newData: DropDownListOptionType[]) =>
    set((state) => ({
      searchDatasourcesList: newData,
    })),

  setSearchPermissionUsersList: (newData: DropDownListOptionType[]) =>
    set((state) => ({
      searchPermissionUsersList: newData,
    })),

  setSearchPermissionGroupsList: (newData: DropDownListOptionType[]) =>
    set((state) => ({
      searchPermissionGroupsList: newData,
    })),

  setGetDatasetListTrigger: (isReset?: boolean) =>
    set((state) => ({
      getDatasetListTrigger: isReset ? 0 : state.getDatasetListTrigger + 1,
    })),

  setSocketData: (newData: SocketDataType) =>
    set((state) => ({
      socketData: {
        ...state.socketData,
        [newData.libraryDetailId]: newData,
      },
    })),

  setSelectedDataset: (dataset: LibraryDataType | null) => set({ selectedDataset: dataset }),

  resetSocketData: () =>
    set((state) => ({
      socketData: {},
    })),
}))

export default useLibraryDataStore
